// components/Register.js
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import API_CONFIG from '../components/API_CONFIG/API_CONFIG';
import { AnimatePresence, motion } from 'framer-motion';
import { 
  FiMail, 
  FiLock, 
  FiEye, 
  FiEyeOff, 
  FiArrowRight,
  FiSmartphone,
  FiUser,
  FiUserPlus,
  FiPhone,
  FiCheck,
  FiX,
  FiCheckCircle,
  FiHome,
  FiShield
} from 'react-icons/fi';

const Register = ({ onLogin }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.classList.add('register-page-active');
    return () => document.body.classList.remove('register-page-active');
  }, []);

  const [isMobile] = useState(window.innerWidth <= 768);
  const navigate = useNavigate();
  const [nom, setNom] = useState("");
  const [prenom, setPrenom] = useState("");
  const [tel, setTel] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState({
    length: false,
    uppercase: false,
    number: false,
    special: false,
    match: true
  });
  const [showScrollIndicator, setShowScrollIndicator] = useState(true);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [userData, setUserData] = useState({});
  const contentRef = useRef(null);

  useEffect(() => {
    const checkScroll = () => {
      if (contentRef.current) {
        const { scrollHeight, clientHeight, scrollTop } = contentRef.current;
        setShowScrollIndicator(scrollHeight - clientHeight > scrollTop + 10);
      }
    };

    if (contentRef.current) {
      contentRef.current.addEventListener('scroll', checkScroll);
      checkScroll();
    }

    return () => {
      if (contentRef.current) {
        contentRef.current.removeEventListener('scroll', checkScroll);
      }
    };
  }, []);

  // Fonction pour valider le mot de passe
  const validatePassword = (password, confirm = confirmPassword) => {
    const errors = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
      match: password === confirm
    };
    setPasswordErrors(errors);
    return Object.values(errors).every(v => v);
  };

  // Validation du numéro de téléphone
  const validatePhone = (phone) => {
    const phoneRegex = /^(\+243|0)[0-9]{9}$/;
    return phoneRegex.test(phone);
  };

  // Validation de l'email
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  useEffect(() => {
    const login = localStorage.getItem("login2");
    if (login) {
      navigate("/dashboard");
    }

    const timeoutId = setTimeout(() => setMsg(""), 5000);
    return () => clearTimeout(timeoutId);
  }, [msg, navigate]);

  const handleInputChange = (e, type) => {
    setError("");
    switch(type) {
      case "nom":
        setNom(e.target.value);
        break;
      case "prenom":
        setPrenom(e.target.value);
        break;
      case "tel":
        setTel(e.target.value);
        break;
      case "email":
        setEmail(e.target.value);
        break;
      case "password":
        setPassword(e.target.value);
        validatePassword(e.target.value, confirmPassword);
        break;
      case "confirmPassword":
        setConfirmPassword(e.target.value);
        validatePassword(password, e.target.value);
        break;
      default:
        break;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validation des champs requis
    if (!email.trim() || !password.trim() || !nom.trim() || !prenom.trim() || !tel.trim()) {
      setError("Tous les champs sont requis");
      return;
    }

    // Validation de l'email
    if (!validateEmail(email)) {
      setError("Veuillez entrer une adresse email valide");
      return;
    }

    // Validation du téléphone
    if (!validatePhone(tel)) {
      setError("Veuillez entrer un numéro de téléphone valide (format: +243 ou 0 suivi de 9 chiffres)");
      return;
    }

    // Vérification de la force du mot de passe
    if (!validatePassword(password, confirmPassword)) {
      setError("Le mot de passe ne respecte pas les exigences de sécurité");
      return;
    }

    // Validation de la confirmation du mot de passe
    if (password !== confirmPassword) {
      setError("Les mots de passe ne correspondent pas");
      return;
    }

    // Vérification des conditions d'utilisation
    if (!termsAccepted) {
      setError("Vous devez accepter les conditions d'utilisation");
      return;
    }

    setLoading(true);
    
    try {
      const response = await fetch(`${API_CONFIG.apiUrl}register_admin.php`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ 
          email, 
          password, 
          nom, 
          prenom, 
          tel 
        }),
      });

      const data = await response.json();
      
      if (data.success) {
        // Sauvegarder les données utilisateur
        setUserData({ 
          nom, 
          prenom, 
          email, 
          tel,
          userId: data.user_id || null 
        });
        
        // Afficher le modal de succès
        setShowSuccessModal(true);
        setMsg("✅ Inscription réussie !");
        
        // Démarrer le compte à rebours pour la redirection vers VerificationOTP
        let counter = 5;
        const interval = setInterval(() => {
          counter -= 1;
          setCountdown(counter);
          if (counter === 0) {
            clearInterval(interval);
            setShowSuccessModal(false);
            // Rediriger vers la page de vérification OTP avec les données utilisateur
            navigate("/verification-otp", { 
              state: { 
                email: email,
                tel: tel,
                nom: nom,
                prenom: prenom,
                userId: data.user_id || null
              } 
            });
          }
        }, 1000);
        
      } else if (data.error) {
        setError(data.error);
      } else {
        setMsg("Inscription réussie ! Vérifiez votre email ou vos sms pour confirmer votre compte.");
        setTimeout(() => navigate("/confirmations"), 2000);
      }
    } catch (error) {
      setError("Une erreur est survenue. Veuillez réessayer.");
      console.error("Registration error:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour rediriger manuellement vers VerificationOTP
  const handleManualRedirect = () => {
    setShowSuccessModal(false);
    navigate("/verification-otp", { 
      state: { 
        email: email,
        tel: tel,
        nom: nom,
        prenom: prenom
      } 
    });
  };

  // Styles CSS complets
  const styles = `
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    body.register-page-active {
      margin: 0;
      padding: 0;
      overflow-x: hidden;
    }

    .register-container {
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
      background: linear-gradient(135deg, #0a0e27 0%, #0a0f2a 100%);
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', sans-serif;
    }

    /* Background avec effets glassmorphism */
    .background-gradient {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      overflow: hidden;
      z-index: 0;
    }

    .gradient-circle {
      position: absolute;
      width: 400px;
      height: 400px;
      border-radius: 50%;
      filter: blur(80px);
      opacity: 0.4;
      animation: float 20s ease-in-out infinite;
    }

    .gradient-circle.top-left {
      background: linear-gradient(135deg, #00b4db, #0083b0);
      top: -100px;
      left: -100px;
      animation-delay: 0s;
    }

    .gradient-circle.bottom-right {
      background: linear-gradient(135deg, #6c63ff, #3f3d9e);
      bottom: -100px;
      right: -100px;
      animation-delay: -10s;
    }

    @keyframes float {
      0%, 100% { transform: translate(0, 0) scale(1); }
      33% { transform: translate(30px, -30px) scale(1.1); }
      66% { transform: translate(-20px, 20px) scale(0.9); }
    }

    /* Carte principale avec glassmorphism */
    .register-card {
      position: relative;
      z-index: 1;
      background: rgba(15, 25, 55, 0.65);
      backdrop-filter: blur(12px);
      border: 1px solid rgba(100, 150, 255, 0.2);
      box-shadow: 0 25px 45px rgba(0, 0, 0, 0.3), 0 0 20px rgba(0, 100, 255, 0.1);
      transition: all 0.3s ease;
      max-height: 90vh;
      overflow-y: auto;
    }

    .register-card::-webkit-scrollbar {
      width: 6px;
    }

    .register-card::-webkit-scrollbar-track {
      background: rgba(100, 150, 255, 0.1);
      border-radius: 3px;
    }

    .register-card::-webkit-scrollbar-thumb {
      background: rgba(0, 180, 255, 0.4);
      border-radius: 3px;
    }

    .register-card::-webkit-scrollbar-thumb:hover {
      background: rgba(0, 180, 255, 0.6);
    }

    .register-card:hover {
      border-color: rgba(100, 150, 255, 0.4);
      box-shadow: 0 30px 55px rgba(0, 0, 0, 0.4), 0 0 30px rgba(0, 150, 255, 0.2);
    }

    .register-card-content {
      padding: 0;
    }

    /* Logo Section */
    .logo-section {
      text-align: center;
      margin-bottom: 40px;
    }

    .logo-wrapper {
      margin-bottom: 25px;
    }

    .logo {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      cursor: pointer;
      margin-bottom: 12px;
    }

    .logo-icon {
      position: relative;
      width: 48px;
      height: 48px;
    }

    .logo-circle {
      width: 48px;
      height: 48px;
      border-radius: 50%;
      background: linear-gradient(135deg, #00d4ff, #0066ff);
      animation: pulse 2s ease-in-out infinite;
    }

    .logo-pulse {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 48px;
      height: 48px;
      border-radius: 50%;
      background: radial-gradient(circle, rgba(0,212,255,0.4) 0%, rgba(0,102,255,0) 70%);
      animation: ripple 2s ease-out infinite;
    }

    @keyframes pulse {
      0%, 100% { transform: scale(1); opacity: 1; }
      50% { transform: scale(1.1); opacity: 0.8; }
    }

    @keyframes ripple {
      0% { transform: translate(-50%, -50%) scale(1); opacity: 0.5; }
      100% { transform: translate(-50%, -50%) scale(2); opacity: 0; }
    }

    .logo-text {
      font-size: 28px;
      font-weight: 700;
      background: linear-gradient(135deg, #fff, #aac8ff);
      -webkit-background-clip: text;
      background-clip: text;
      color: transparent;
      letter-spacing: -0.5px;
    }

    .logo-subtitle {
      font-size: 14px;
      color: rgba(170, 200, 255, 0.7);
      font-weight: 400;
    }

    /* Messages */
    .message-container {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 12px 16px;
      border-radius: 12px;
      margin-bottom: 20px;
      backdrop-filter: blur(8px);
      animation: slideIn 0.3s ease;
    }

    .message-container.error {
      background: rgba(255, 68, 68, 0.15);
      border: 1px solid rgba(255, 68, 68, 0.3);
    }

    .message-container.success {
      background: rgba(68, 255, 68, 0.15);
      border: 1px solid rgba(68, 255, 68, 0.3);
    }

    .message-icon {
      font-size: 20px;
    }

    .message-text {
      color: rgba(255, 255, 255, 0.95);
      font-size: 14px;
      flex: 1;
      text-align: left;
    }

    @keyframes slideIn {
      from {
        opacity: 0;
        transform: translateY(-10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    /* Formulaire */
    .register-form {
      display: flex;
      flex-direction: column;
      gap: 24px;
    }

    .name-fields {
      display: flex;
      gap: 16px;
      margin-bottom: 0;
    }

    .form-group {
      display: flex;
      flex-direction: column;
      gap: 8px;
      flex: 1;
    }

    .form-group.half {
      flex: 1;
    }

    .form-label {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 14px;
      font-weight: 500;
      color: rgba(170, 200, 255, 0.9);
      letter-spacing: 0.3px;
    }

    .input-icon {
      font-size: 16px;
    }

    .input-wrapper {
      position: relative;
      width: 100%;
    }

    .form-input {
      width: 100%;
      padding: 14px 16px;
      background: rgba(10, 20, 45, 0.6);
      border: 1.5px solid rgba(100, 150, 255, 0.2);
      border-radius: 12px;
      font-size: 15px;
      color: #ffffff;
      transition: all 0.3s ease;
      outline: none;
      font-family: inherit;
    }

    .form-input:focus {
      border-color: rgba(0, 180, 255, 0.6);
      background: rgba(10, 20, 45, 0.8);
      box-shadow: 0 0 15px rgba(0, 180, 255, 0.2);
    }

    .form-input.input-error {
      border-color: rgba(255, 68, 68, 0.6);
    }

    .form-input::placeholder {
      color: rgba(170, 200, 255, 0.4);
    }

    .input-hint {
      font-size: 11px;
      color: rgba(170, 200, 255, 0.5);
      margin-top: 4px;
    }

    .error-text {
      font-size: 11px;
      color: #ff4444;
      margin-top: 4px;
    }

    .password-toggle {
      position: absolute;
      right: 12px;
      top: 50%;
      transform: translateY(-50%);
      background: none;
      border: none;
      color: rgba(170, 200, 255, 0.6);
      cursor: pointer;
      font-size: 18px;
      padding: 4px;
      transition: color 0.3s ease;
    }

    .password-toggle:hover {
      color: rgba(0, 180, 255, 0.9);
    }

    /* Password strength */
    .password-strength {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 8px;
      margin-top: 8px;
      padding: 12px;
      background: rgba(0, 0, 0, 0.2);
      border-radius: 10px;
    }

    .strength-item {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 12px;
      color: rgba(170, 200, 255, 0.7);
    }

    .strength-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 18px;
      height: 18px;
      border-radius: 50%;
      font-size: 11px;
    }

    .strength-icon.valid {
      color: #00ff88;
    }

    .strength-icon.invalid {
      color: #ff4444;
    }

    .strength-text {
      font-size: 11px;
    }

    /* Checkbox */
    .checkbox-container {
      display: flex;
      align-items: flex-start;
      gap: 10px;
      cursor: pointer;
      font-size: 13px;
      color: rgba(170, 200, 255, 0.8);
    }

    .checkbox-container.terms {
      align-items: flex-start;
    }

    .checkbox-input {
      width: 18px;
      height: 18px;
      cursor: pointer;
      accent-color: #00b4ff;
      margin-top: 2px;
    }

    .checkbox-label {
      user-select: none;
      line-height: 1.4;
    }

    .terms-link {
      color: #00b4ff;
      text-decoration: none;
      transition: all 0.3s ease;
    }

    .terms-link:hover {
      color: #00d4ff;
      text-decoration: underline;
    }

    /* Bouton d'inscription */
    .register-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      padding: 14px 24px;
      background: linear-gradient(135deg, rgba(0, 100, 255, 0.8), rgba(0, 50, 150, 0.8));
      border: 1px solid rgba(0, 180, 255, 0.4);
      border-radius: 12px;
      font-size: 16px;
      font-weight: 600;
      color: white;
      cursor: pointer;
      transition: all 0.3s ease;
      backdrop-filter: blur(4px);
      margin-top: 8px;
    }

    .register-btn:hover:not(:disabled) {
      background: linear-gradient(135deg, rgba(0, 120, 255, 0.9), rgba(0, 70, 180, 0.9));
      border-color: rgba(0, 200, 255, 0.6);
      box-shadow: 0 0 20px rgba(0, 150, 255, 0.3);
      transform: translateY(-2px);
    }

    .register-btn:disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }

    .btn-arrow {
      font-size: 18px;
    }

    /* Séparateur */
    .separator {
      display: flex;
      align-items: center;
      text-align: center;
      margin: 8px 0;
    }

    .separator::before,
    .separator::after {
      content: '';
      flex: 1;
      border-bottom: 1px solid rgba(100, 150, 255, 0.2);
    }

    .separator-text {
      padding: 0 16px;
      font-size: 12px;
      color: rgba(170, 200, 255, 0.6);
      text-transform: uppercase;
    }

    /* Lien de connexion */
    .login-link {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      padding: 12px;
      background: rgba(0, 100, 255, 0.1);
      border: 1px solid rgba(0, 180, 255, 0.2);
      border-radius: 12px;
      text-decoration: none;
      color: rgba(170, 200, 255, 0.9);
      font-size: 14px;
      font-weight: 500;
      transition: all 0.3s ease;
    }

    .login-link:hover {
      background: rgba(0, 150, 255, 0.15);
      border-color: rgba(0, 200, 255, 0.4);
      color: #ffffff;
      transform: translateY(-2px);
    }

    .login-icon {
      font-size: 18px;
    }

    /* Scroll indicator */
    .scroll-indicator {
      position: absolute;
      bottom: 20px;
      left: 50%;
      transform: translateX(-50%);
      opacity: 0;
      transition: opacity 0.3s ease;
      pointer-events: none;
    }

    .scroll-indicator.visible {
      opacity: 1;
    }

    .scroll-arrow {
      width: 30px;
      height: 30px;
      background: rgba(0, 180, 255, 0.3);
      backdrop-filter: blur(4px);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-size: 18px;
      animation: bounce 2s ease-in-out infinite;
    }

    @keyframes bounce {
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(10px); }
    }

    /* Footer mobile */
    .mobile-footer {
      margin-top: 30px;
      padding-top: 20px;
      text-align: center;
      border-top: 1px solid rgba(100, 150, 255, 0.1);
    }

    .mobile-footer-text {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      font-size: 12px;
      color: rgba(170, 200, 255, 0.5);
      margin-bottom: 8px;
    }

    .mobile-footer-version {
      font-size: 10px;
      color: rgba(170, 200, 255, 0.3);
    }

    /* Footer desktop */
    .desktop-footer {
      margin-top: 40px;
      padding-top: 20px;
      text-align: center;
      border-top: 1px solid rgba(100, 150, 255, 0.1);
    }

    .copyright {
      font-size: 11px;
      color: rgba(170, 200, 255, 0.4);
      margin-bottom: 12px;
    }

    .footer-links {
      display: flex;
      justify-content: center;
      gap: 20px;
    }

    .footer-links a {
      font-size: 11px;
      color: rgba(170, 200, 255, 0.5);
      text-decoration: none;
      transition: color 0.3s ease;
    }

    .footer-links a:hover {
      color: #00b4ff;
    }

    /* Mobile App Header */
    .mobile-app-header {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      z-index: 10;
    }

    .mobile-status-bar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 12px 20px;
      background: rgba(0, 0, 0, 0.2);
      backdrop-filter: blur(10px);
    }

    .mobile-time {
      font-size: 14px;
      font-weight: 600;
      color: white;
    }

    .mobile-status-icons {
      display: flex;
      gap: 6px;
    }

    .mobile-signal, .mobile-wifi, .mobile-battery {
      display: inline-block;
      width: 16px;
      height: 10px;
      background: rgba(255, 255, 255, 0.8);
      border-radius: 2px;
    }

    .mobile-nav-bar {
      height: 44px;
      background: rgba(0, 0, 0, 0.1);
      backdrop-filter: blur(10px);
    }

    /* Modal de confirmation SUCCÈS - VERT */
    .success-modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.85);
      backdrop-filter: blur(12px);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 9999;
      padding: 20px;
    }

    .success-modal {
      background: linear-gradient(145deg, #0a0e27, #0d1f3c);
      border: 2px solid rgba(0, 255, 100, 0.4);
      border-radius: 28px;
      padding: 48px;
      max-width: 500px;
      width: 100%;
      text-align: center;
      box-shadow: 0 40px 80px rgba(0, 0, 0, 0.6), 0 0 60px rgba(0, 255, 100, 0.15);
      position: relative;
      overflow: hidden;
    }

    .success-modal::before {
      content: '';
      position: absolute;
      top: -50%;
      left: -50%;
      width: 200%;
      height: 200%;
      background: radial-gradient(circle at center, rgba(0, 255, 100, 0.05) 0%, transparent 70%);
      animation: rotateGlow 20s linear infinite;
    }

    @keyframes rotateGlow {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }

    .success-icon-wrapper {
      width: 120px;
      height: 120px;
      margin: 0 auto 28px;
      position: relative;
      z-index: 1;
    }

    .success-icon-circle {
      width: 120px;
      height: 120px;
      border-radius: 50%;
      background: linear-gradient(135deg, #00ff88, #00cc66);
      display: flex;
      align-items: center;
      justify-content: center;
      animation: successPulse 2s ease-in-out infinite;
      box-shadow: 0 0 40px rgba(0, 255, 136, 0.3);
    }

    .success-icon-circle svg {
      width: 60px;
      height: 60px;
      color: white;
      stroke: white;
      stroke-width: 2;
    }

    @keyframes successPulse {
      0%, 100% { 
        transform: scale(1); 
        box-shadow: 0 0 20px rgba(0, 255, 136, 0.3);
      }
      50% { 
        transform: scale(1.05); 
        box-shadow: 0 0 60px rgba(0, 255, 136, 0.5);
      }
    }

    .success-checkmark {
      stroke-dasharray: 100;
      stroke-dashoffset: 100;
      animation: drawCheckmark 0.8s ease-out forwards;
    }

    @keyframes drawCheckmark {
      0% { stroke-dashoffset: 100; }
      100% { stroke-dashoffset: 0; }
    }

    .success-title {
      font-size: 32px;
      font-weight: 700;
      color: #00ff88;
      margin-bottom: 12px;
      position: relative;
      z-index: 1;
      text-shadow: 0 0 30px rgba(0, 255, 136, 0.2);
    }

    .success-subtitle {
      font-size: 16px;
      color: rgba(255, 255, 255, 0.7);
      margin-bottom: 28px;
      position: relative;
      z-index: 1;
      line-height: 1.6;
    }

    .success-details {
      background: rgba(0, 255, 136, 0.08);
      border: 1px solid rgba(0, 255, 136, 0.2);
      border-radius: 16px;
      padding: 20px;
      margin-bottom: 28px;
      text-align: left;
      position: relative;
      z-index: 1;
    }

    .success-detail-item {
      display: flex;
      align-items: center;
      gap: 14px;
      color: rgba(255, 255, 255, 0.85);
      font-size: 15px;
      padding: 10px 0;
    }

    .success-detail-item:not(:last-child) {
      border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    }

    .success-detail-item svg {
      color: #00ff88;
      font-size: 18px;
      min-width: 20px;
    }

    .success-detail-item .label {
      color: rgba(255, 255, 255, 0.5);
      font-size: 12px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      min-width: 80px;
    }

    .success-detail-item .value {
      color: #ffffff;
      font-weight: 500;
    }

    .countdown-section {
      margin-bottom: 24px;
      position: relative;
      z-index: 1;
    }

    .countdown-bar {
      width: 100%;
      height: 6px;
      background: rgba(255, 255, 255, 0.1);
      border-radius: 3px;
      overflow: hidden;
      margin-bottom: 12px;
    }

    .countdown-progress {
      height: 100%;
      background: linear-gradient(90deg, #00ff88, #00cc66);
      border-radius: 3px;
      transition: width 1s linear;
      box-shadow: 0 0 20px rgba(0, 255, 136, 0.3);
    }

    .countdown-text {
      color: rgba(255, 255, 255, 0.6);
      font-size: 14px;
    }

    .countdown-number {
      color: #00ff88;
      font-weight: 700;
      font-size: 22px;
      animation: countdownPulse 1s ease-in-out infinite;
    }

    @keyframes countdownPulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.5; }
    }

    .success-actions {
      display: flex;
      gap: 12px;
      justify-content: center;
      position: relative;
      z-index: 1;
    }

    .success-btn-primary {
      flex: 1;
      padding: 14px 32px;
      background: linear-gradient(135deg, #00ff88, #00cc66);
      border: none;
      border-radius: 14px;
      color: #0a0e27;
      font-weight: 700;
      font-size: 16px;
      cursor: pointer;
      transition: all 0.3s ease;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      box-shadow: 0 4px 15px rgba(0, 255, 136, 0.2);
    }

    .success-btn-primary:hover {
      transform: translateY(-3px);
      box-shadow: 0 8px 30px rgba(0, 255, 136, 0.4);
    }

    .success-btn-primary:active {
      transform: translateY(0);
    }

    .success-btn-secondary {
      flex: 1;
      padding: 14px 32px;
      background: rgba(255, 255, 255, 0.08);
      border: 1px solid rgba(255, 255, 255, 0.15);
      border-radius: 14px;
      color: white;
      font-weight: 600;
      font-size: 16px;
      cursor: pointer;
      transition: all 0.3s ease;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
    }

    .success-btn-secondary:hover {
      background: rgba(255, 255, 255, 0.15);
      transform: translateY(-3px);
      border-color: rgba(255, 255, 255, 0.3);
    }

    .otp-redirect-badge {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      background: rgba(0, 255, 136, 0.15);
      border: 1px solid rgba(0, 255, 136, 0.3);
      border-radius: 20px;
      padding: 6px 16px;
      font-size: 13px;
      color: #00ff88;
      margin-top: 8px;
    }

    /* Responsive */
    @media (max-width: 768px) {
      .register-card {
        border-radius: 0 !important;
        min-height: 100vh !important;
        width: 100% !important;
        background: rgba(15, 25, 55, 0.8);
        max-height: 100vh;
      }

      .gradient-circle {
        width: 250px;
        height: 250px;
      }

      .name-fields {
        flex-direction: column;
        gap: 20px;
      }

      .password-strength {
        grid-template-columns: 1fr;
      }

      .success-modal {
        padding: 32px 24px;
        margin: 16px;
        border-radius: 20px;
      }

      .success-icon-wrapper {
        width: 90px;
        height: 90px;
        margin-bottom: 20px;
      }

      .success-icon-circle {
        width: 90px;
        height: 90px;
      }

      .success-icon-circle svg {
        width: 45px;
        height: 45px;
      }

      .success-title {
        font-size: 26px;
      }

      .success-subtitle {
        font-size: 14px;
        margin-bottom: 20px;
      }

      .success-details {
        padding: 16px;
        margin-bottom: 20px;
      }

      .success-detail-item {
        font-size: 13px;
        padding: 8px 0;
      }

      .success-detail-item .label {
        min-width: 60px;
        font-size: 11px;
      }

      .success-actions {
        flex-direction: column;
      }

      .success-btn-primary,
      .success-btn-secondary {
        width: 100%;
        padding: 12px 20px;
        font-size: 15px;
      }

      .countdown-number {
        font-size: 18px;
      }
    }
  `;

  return (
    <div className="register-container">
      <style>{styles}</style>
      
      {/* Background avec effet moderne */}
      <div className="background-gradient">
        <div className="gradient-circle top-left"></div>
        <div className="gradient-circle bottom-right"></div>
        {isMobile && (
          <div className="mobile-app-header">
            <div className="mobile-status-bar">
              <span className="mobile-time">{new Date().getHours()}:{new Date().getMinutes().toString().padStart(2, '0')}</span>
              <div className="mobile-status-icons">
                <span className="mobile-signal"></span>
                <span className="mobile-wifi"></span>
                <span className="mobile-battery"></span>
              </div>
            </div>
            <div className="mobile-nav-bar"></div>
          </div>
        )}
      </div>
      
      <motion.div 
        className="register-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        style={{
          maxWidth: isMobile ? '100%' : '680px',
          borderRadius: isMobile ? '0' : '24px',
          minHeight: isMobile ? '100vh' : 'auto',
          padding: isMobile ? '40px 20px' : '40px',
        }}
      >
        <div className="register-card-content" ref={contentRef}>
          {/* Logo Section */}
          <motion.div 
            className="logo-section"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <div className="logo-wrapper">
              <motion.div 
                className="logo"
                whileHover={{ scale: 1.05 }}
              >
                <div className="logo-icon">
                  <div className="logo-circle"></div>
                  <div className="logo-pulse"></div>
                </div>
                <h1 className="logo-text">RefisC-RDC</h1>
              </motion.div>
              <p className="logo-subtitle">Créez votre compte</p>
            </div>

            <AnimatePresence>
              {(error || msg) && (
                <motion.div
                  initial={{ opacity: 0, height: 0, y: -10 }}
                  animate={{ opacity: 1, height: 'auto', y: 0 }}
                  exit={{ opacity: 0, height: 0 }}
                  className={`message-container ${error ? 'error' : 'success'}`}
                >
                  <div className="message-icon">
                    {error ? '⚠️' : '✅'}
                  </div>
                  <p className="message-text">{error || msg}</p>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Register Form */}
          <motion.form 
            className="register-form"
            onSubmit={handleSubmit}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            {/* Nom et Prénom */}
            <div className="name-fields">
              <div className="form-group half">
                <label htmlFor="nom" className="form-label">
                  <FiUser className="input-icon" />
                  <span>* Nom</span>
                </label>
                <div className="input-wrapper">
                  <input
                    type="text"
                    id="nom"
                    name="nom"
                    value={nom}
                    onChange={(e) => handleInputChange(e, "nom")}
                    className="form-input"
                    placeholder="Votre nom"
                    autoComplete="family-name"
                    required
                  />
                </div>
              </div>

              <div className="form-group half">
                <label htmlFor="prenom" className="form-label">
                  <FiUser className="input-icon" />
                  <span>* Prénom</span>
                </label>
                <div className="input-wrapper">
                  <input
                    type="text"
                    id="prenom"
                    name="prenom"
                    value={prenom}
                    onChange={(e) => handleInputChange(e, "prenom")}
                    className="form-input"
                    placeholder="Votre prénom"
                    autoComplete="given-name"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Téléphone et Email */}
            <div className="name-fields">
              <div className="form-group half">
                <label htmlFor="tel" className="form-label">
                  <FiPhone className="input-icon" />
                  <span>* Téléphone</span>
                </label>
                <div className="input-wrapper">
                  <input
                    type="tel"
                    id="tel"
                    name="tel"
                    value={tel}
                    onChange={(e) => handleInputChange(e, "tel")}
                    className="form-input"
                    placeholder="+243 XXX XXX XXX"
                    autoComplete="tel"
                    required
                  />
                </div>
                <p className="input-hint">Format: +243 ou 0 suivi de 9 chiffres</p>
              </div>

              <div className="form-group half">
                <label htmlFor="email" className="form-label">
                  <FiMail className="input-icon" />
                  <span>Email</span>
                </label>
                <div className="input-wrapper">
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={email}
                    onChange={(e) => handleInputChange(e, "email")}
                    className="form-input"
                    placeholder="votre@email.com"
                    autoComplete="email"
                  />
                </div>
              </div>
            </div>

            {/* Mots de passe */}
            <div className="name-fields">
              <div className="form-group half">
                <label htmlFor="password" className="form-label">
                  <FiLock className="input-icon" />
                  <span>* Mot de passe</span>
                </label>
                <div className="input-wrapper">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    value={password}
                    onChange={(e) => handleInputChange(e, "password")}
                    className="form-input"
                    placeholder="••••••••"
                    autoComplete="new-password"
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? "Cacher le mot de passe" : "Afficher le mot de passe"}
                  >
                    {showPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>

              <div className="form-group half">
                <label htmlFor="confirmPassword" className="form-label">
                  <FiLock className="input-icon" />
                  <span>Confirmer le mot de passe</span>
                </label>
                <div className="input-wrapper">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => handleInputChange(e, "confirmPassword")}
                    className={`form-input ${confirmPassword && !passwordErrors.match ? 'input-error' : ''}`}
                    placeholder="••••••••"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    aria-label={showConfirmPassword ? "Cacher le mot de passe" : "Afficher le mot de passe"}
                  >
                    {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
                {confirmPassword && !passwordErrors.match && (
                  <p className="error-text">Les mots de passe ne correspondent pas</p>
                )}
              </div>
            </div>

            {/* Indicateurs de force du mot de passe */}
            {password && (
              <div className="password-strength">
                <div className="strength-item">
                  <span className={`strength-icon ${passwordErrors.length ? 'valid' : 'invalid'}`}>
                    {passwordErrors.length ? <FiCheck /> : <FiX />}
                  </span>
                  <span className="strength-text">8 caractères minimum</span>
                </div>
                <div className="strength-item">
                  <span className={`strength-icon ${passwordErrors.uppercase ? 'valid' : 'invalid'}`}>
                    {passwordErrors.uppercase ? <FiCheck /> : <FiX />}
                  </span>
                  <span className="strength-text">1 majuscule</span>
                </div>
                <div className="strength-item">
                  <span className={`strength-icon ${passwordErrors.number ? 'valid' : 'invalid'}`}>
                    {passwordErrors.number ? <FiCheck /> : <FiX />}
                  </span>
                  <span className="strength-text">1 chiffre</span>
                </div>
                <div className="strength-item">
                  <span className={`strength-icon ${passwordErrors.special ? 'valid' : 'invalid'}`}>
                    {passwordErrors.special ? <FiCheck /> : <FiX />}
                  </span>
                  <span className="strength-text">1 caractère spécial</span>
                </div>
              </div>
            )}

            <div className="form-group">
              <label className="checkbox-container terms">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="checkbox-input"
                  required
                />
                <span className="checkbox-label">
                  J'accepte les <a href="/terms" className="terms-link">conditions d'utilisation</a> et la <a href="/privacy" className="terms-link">politique de confidentialité</a>
                </span>
              </label>
            </div>

            <motion.button 
              type="submit" 
              className="register-btn"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={loading}
            >
              <span>{loading ? 'Inscription en cours...' : "S'INSCRIRE"}</span>
              {!loading && (
                <motion.div 
                  className="btn-arrow"
                  initial={{ x: -5 }}
                  animate={{ x: 0 }}
                  transition={{ repeat: Infinity, repeatType: "reverse", duration: 0.8 }}
                >
                  <FiArrowRight />
                </motion.div>
              )}
            </motion.button>

            {/* Séparateur */}
            <div className="separator">
              <span className="separator-text">Déjà un compte ?</span>
            </div>

            {/* Lien pour se connecter */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              <Link to="/" className="login-link">
                <FiUserPlus className="login-icon" />
                <span>Se connecter</span>
              </Link>
            </motion.div>

            {isMobile && (
              <div className="mobile-footer">
                <p className="mobile-footer-text">
                  <FiSmartphone />
                  <span>Application mobile RefisC-RDC</span>
                </p>
                <div className="mobile-footer-version">
                  Version 1.0.0
                </div>
              </div>
            )}
          </motion.form>
        </div>

        <div className={`scroll-indicator ${showScrollIndicator ? 'visible' : ''}`}>
          <div className="scroll-arrow">↓</div>
        </div>

        {/* Footer pour desktop */}
        {!isMobile && (
          <div className="desktop-footer">
            <p className="copyright">© 2024 RefisC-RDC. Tous droits réservés.</p>
            <div className="footer-links">
              <a href="/privacy">Confidentialité</a>
              <a href="/terms">Conditions</a>
              <a href="/help">Aide</a>
            </div>
          </div>
        )}
      </motion.div>

      {/* MODAL DE CONFIRMATION - VERT avec redirection vers VerificationOTP */}
      <AnimatePresence>
        {showSuccessModal && (
          <motion.div 
            className="success-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                // Ne pas fermer en cliquant à l'extérieur
              }
            }}
          >
            <motion.div 
              className="success-modal"
              initial={{ scale: 0.8, y: 30, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.8, y: 30, opacity: 0 }}
              transition={{ type: "spring", damping: 20, stiffness: 300 }}
            >
              {/* Icône de succès */}
              <div className="success-icon-wrapper">
                <div className="success-icon-circle">
                  <svg viewBox="0 0 24 24" className="success-checkmark">
                    <path 
                      d="M20 6L9 17L4 12" 
                      stroke="white" 
                      strokeWidth="3" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                      fill="none"
                    />
                  </svg>
                </div>
              </div>

              {/* Titre et sous-titre */}
              <h2 className="success-title">🎉 Inscription réussie !</h2>
              <p className="success-subtitle">
                Votre compte a été créé avec succès. Vous allez être redirigé(e) vers la page de vérification OTP pour sécuriser votre compte.
              </p>

              {/* Badge OTP */}
              <div className="otp-redirect-badge">
                <FiShield />
                <span>Vérification OTP requise</span>
              </div>

              {/* Détails de l'utilisateur */}
              <div className="success-details">
                <div className="success-detail-item">
                  <FiUser />
                  <span className="label">Nom complet</span>
                  <span className="value">{prenom} {nom}</span>
                </div>
                <div className="success-detail-item">
                  <FiMail />
                  <span className="label">Email</span>
                  <span className="value">{email}</span>
                </div>
                <div className="success-detail-item">
                  <FiPhone />
                  <span className="label">Téléphone</span>
                  <span className="value">{tel}</span>
                </div>
              </div>

              {/* Compte à rebours */}
              <div className="countdown-section">
                <div className="countdown-bar">
                  <div 
                    className="countdown-progress" 
                    style={{ width: `${(countdown / 5) * 100}%` }}
                  />
                </div>
                <p className="countdown-text">
                  Redirection vers la vérification OTP dans <span className="countdown-number">{countdown}</span> seconde{countdown > 1 ? 's' : ''}
                </p>
              </div>

              {/* Actions */}
              <div className="success-actions">
                <button 
                  className="success-btn-primary"
                  onClick={handleManualRedirect}
                >
                  <FiShield />
                  Vérifier maintenant
                </button>
                <button 
                  className="success-btn-secondary"
                  onClick={() => navigate('/')}
                >
                  <FiHome />
                  Accueil
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Register;